/**
 * Fetches the CPT-codes mapping document for a given organisation from
 * Firestore (`/healthcare_facility/{orgId}/cpt_codes_mapping/v1`).
 *
 * The Firestore document wraps the mapping array under a `cpt_codes` field
 * (per the seeder in `seedCptCodesMappingToFirestore.ts`). This hook
 * normalises the payload so consumers always receive a plain
 * {@link CptCodesMappingModel} array (empty when the doc is missing).
 *
 * @module useCptCodesMappingForAnOrg
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { FirestoreService } from "../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../api/firebase/references";
import { CptCodesMappingModel } from "../data-model/cptCodesMapping";

/**
 * `{ label, value }` pair derived from a {@link CptCodesMappingModel}.
 * Both fields hold the same `cpt_description` string so the option set is
 * directly consumable by selection controls (Oasis `SearchableCard`,
 * native `<select>`, etc.) without further transformation.
 */
export interface CptCodesMappingOption {
  label: string;
  value: string;
}

/** Raw Firestore document shape stored at `cpt_codes_mapping/v1`. */
interface CptCodesMappingDocument {
  cpt_codes?: CptCodesMappingModel[];
}

/**
 * State returned by {@link useCptCodesMappingForAnOrg}.
 *
 * @property data - The fetched mapping rows; `undefined` until the first
 *   successful response, `[]` when the doc exists but is empty.
 * @property loading - True while a fetch is in flight.
 * @property error - Error message from the most recent failed fetch; `null`
 *   on success or while a new fetch is starting.
 */
export interface UseCptCodesMappingForAnOrgData {
  data?: CptCodesMappingModel[];
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook for fetching the CPT-codes mapping for a single organisation.
 *
 * Auto-fetches on mount and whenever `organizationId` changes. Also exposes
 * a `refetch` function that callers can invoke on demand (e.g. after a
 * seeder runs or the user uploads a new mapping).
 *
 * Defensive against missing documents: if Firestore reports the doc does
 * not exist, the hook resolves with `data: []` rather than surfacing an
 * error so the UI can render an empty state without special-casing.
 *
 * Also derives a `cptCodesMappingOptions` array — each row mapped to a
 * `{ label, value }` pair where both fields hold the row's `cpt_description`
 * — for direct use with selection controls. The derivation is memoised
 * against the fetched data so consumers can pass it as a stable prop.
 *
 * @param organizationId - The facility / org id used in the doc path.
 * @returns `cptCodesMappingData` (raw state), `cptCodesMappingOptions`
 *   (memoised `{label, value}[]`), and a `refetch` trigger.
 *
 * @example
 * const {
 *   cptCodesMappingData,
 *   cptCodesMappingOptions,
 *   refetchCptCodesMapping,
 * } = useCptCodesMappingForAnOrg(orgId);
 *
 * if (cptCodesMappingData.loading) return <Spinner />;
 * if (cptCodesMappingData.error) return <Error msg={cptCodesMappingData.error} />;
 *
 * return <SearchableCard options={cptCodesMappingOptions} />;
 */
export const useCptCodesMappingForAnOrg = (organizationId: string) => {
  const [cptCodesMappingData, setCptCodesMappingData] =
    useState<UseCptCodesMappingForAnOrgData>({
      data: undefined,
      loading: false,
      error: null,
    });

  const fetchCptCodesMapping = useCallback(async () => {
    if (!organizationId) {
      setCptCodesMappingData({
        data: [],
        loading: false,
        error: null,
      });
      return;
    }

    setCptCodesMappingData((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const doc = await FirestoreService.getDocument<CptCodesMappingDocument>(
        FirestoreDocumentReference.cptCodesMapping(organizationId),
      );
      setCptCodesMappingData({
        data: doc?.cpt_codes ?? [],
        loading: false,
        error: null,
      });
    } catch (error) {
      console.log("[hook] caught error:", error);
      const message =
        error instanceof Error ? error.message : String(error ?? "");

      // Firestore's "Document does not exist!" should not be a hard error —
      // surface as an empty mapping so callers can render an empty state.
      if (message.toLowerCase().includes("does not exist")) {
        setCptCodesMappingData({
          data: [],
          loading: false,
          error: null,
        });
        return;
      }

      setCptCodesMappingData({
        data: undefined,
        loading: false,
        error: message || "Failed to fetch CPT codes mapping",
      });
    }
  }, [organizationId]);

  useEffect(() => {
    fetchCptCodesMapping();
  }, [fetchCptCodesMapping, organizationId]);

  const cptCodesMappingOptions = useMemo<CptCodesMappingOption[]>(
    () =>
      (cptCodesMappingData.data ?? []).map((row) => ({
        label: row.cpt_description,
        value: row.cpt_description,
      })),
    [cptCodesMappingData.data],
  );

  return {
    cptCodesMappingData,
    cptCodesMappingOptions,
    refetchCptCodesMapping: fetchCptCodesMapping,
  };
};
